<template>
  <div class="admin-dom-wrapper">
    <admin-nav active="dossiers" />

    <b-container fluid>
      <div class="d-flex align-items-center mb-2">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="retour"
        >
          ← Retour à la liste
        </b-button>
        <h4 class="mb-0 ml-2">
          Dossier {{ dossier ? dossier.C_raisonSociale || (`${dossier.C_prenom} ${dossier.C_nom}`) : '' }}
        </h4>
        <span
          v-if="dossier && dossier.B_archive"
          class="ml-2"
        >
          <b-badge variant="dark">Archivé</b-badge>
        </span>
        <span
          v-if="dossier && dossier.B_traite"
          class="ml-1"
        >
          <b-badge variant="success">Traité</b-badge>
        </span>
      </div>

      <div v-if="dossier">
        <b-card class="mb-2">
          <b-row>
            <b-col md="8">
              <label>Note interne</label>
              <b-form-textarea
                v-model="note"
                rows="2"
                placeholder="Note visible uniquement par l'admin"
              />
            </b-col>
            <b-col
              md="4"
              class="d-flex flex-column justify-content-end"
            >
              <b-button
                v-if="dossier.I_etat < 6 && dossier.B_archive === 0"
                variant="outline-primary"
                class="mb-1"
                :disabled="relanceEnCours"
                @click="relancerClient"
              >
                <b-spinner
                  v-if="relanceEnCours"
                  small
                />
                <span v-else>Relancer le client</span>
              </b-button>
              <b-button
                variant="success"
                :disabled="dossier.B_traite === 1"
                class="mb-1"
                @click="marquerTraite"
              >
                Marquer traité
              </b-button>
              <b-button
                v-if="dossier.B_traite === 1 && dossier.B_archive === 0"
                variant="outline-warning"
                class="mb-1"
                @click="annulerTraitement"
              >
                Annuler traitement
              </b-button>
              <b-button
                variant="danger"
                :disabled="dossier.B_archive === 1"
                @click="showArchiveModal = true"
              >
                Archiver (supprime les pièces jointes)
              </b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-tabs>
          <b-tab
            title="Informations"
            active
          >
            <b-card>
              <h5>Client</h5>
              <b-row>
                <b-col md="6"><strong>Nom :</strong> {{ dossier.C_prenom }} {{ dossier.C_nom }}</b-col>
                <b-col md="6"><strong>Email :</strong> {{ dossier.C_email }}</b-col>
                <b-col md="6"><strong>Téléphone :</strong> {{ dossier.C_port }}</b-col>
                <b-col md="6"><strong>Type client :</strong> {{ dossier.C_typeClient }}</b-col>
                <b-col md="6"><strong>Situation pro :</strong> {{ dossier.C_situationPro }}</b-col>
                <b-col md="6"><strong>Type de demande :</strong> {{ dossier.C_typeDemande }}</b-col>
              </b-row>
            </b-card>

            <b-card class="mt-2">
              <h5>Établissement</h5>
              <b-row>
                <b-col md="6"><strong>Raison sociale :</strong> {{ dossier.C_raisonSociale }}</b-col>
                <b-col md="6"><strong>Forme juridique :</strong> {{ dossier.C_formeJuridique }}</b-col>
                <b-col md="6"><strong>Capital social :</strong> {{ dossier.C_capitalSocial }}</b-col>
                <b-col md="6"><strong>Code APE :</strong> {{ dossier.C_codeAPE }}</b-col>
                <b-col md="12"><strong>Objet social :</strong> {{ dossier.C_objetSocial }}</b-col>
                <b-col md="12"><strong>Activité exercée :</strong> {{ dossier.C_activiteExercee }}</b-col>
                <b-col md="6"><strong>Email pro :</strong> {{ dossier.C_emailPro }}</b-col>
                <b-col md="6"><strong>Téléphone pro :</strong> {{ dossier.C_telPro }}</b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="dossier.B_associes"
              class="mt-2"
            >
              <h5>Associés (> 25 % des parts)</h5>
              <ul>
                <li
                  v-for="a in dossier.associes"
                  :key="a.UI"
                >
                  {{ a.prenom }} {{ a.nom }} — {{ a.pourcentageParts }} %
                </li>
              </ul>
            </b-card>

            <b-card class="mt-2">
              <h5>Représentant légal</h5>
              <b-row>
                <b-col md="6"><strong>Qualité :</strong> {{ dossier.C_qualite }}</b-col>
                <b-col md="6"><strong>Civilité :</strong> {{ dossier.C_civilite }}</b-col>
                <b-col md="6"><strong>Nom :</strong> {{ dossier.C_prenomRL }} {{ dossier.C_nomRL }}</b-col>
                <b-col md="6"><strong>Email :</strong> {{ dossier.C_emailRL }}</b-col>
                <b-col md="6"><strong>Téléphone :</strong> {{ dossier.C_portableRL }}</b-col>
                <b-col md="6"><strong>Date de naissance :</strong> {{ formatDate(dossier.D_dateNaissance, true) }}</b-col>
                <b-col md="12"><strong>Lieu de naissance :</strong> {{ dossier.C_villeNaissance }} ({{ dossier.C_deptNaissance }}) — {{ dossier.C_paysNaissance }}</b-col>
                <b-col md="12"><strong>Adresse :</strong> {{ dossier.C_adresseRL }} {{ dossier.C_complementAdresse }}, {{ dossier.C_cpRL }} {{ dossier.C_villeRL }}, {{ dossier.C_paysRL }}</b-col>
                <b-col md="6">
                  <strong>Résident fiscal étranger :</strong>
                  {{ dossier.B_residentFiscalEtranger ? `Oui (${dossier.C_paysResidenceFiscale})` : 'Non' }}
                </b-col>
                <b-col md="6">
                  <strong>Personne exposée :</strong>
                  {{ dossier.B_personneExposee ? 'Oui' : 'Non' }}
                </b-col>
                <b-col
                  v-if="dossier.C_commentaire"
                  md="12"
                >
                  <strong>Commentaire :</strong> {{ dossier.C_commentaire }}
                </b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="dossier.formule"
              class="mt-2"
            >
              <h5>Formule choisie</h5>
              <b-row>
                <b-col md="6"><strong>Nom :</strong> {{ dossier.formule.nom }}</b-col>
                <b-col md="6"><strong>Pack :</strong> {{ dossier.formule.pack }}</b-col>
                <b-col md="6"><strong>Prix mensuel HT :</strong> {{ dossier.formule.prixMensuelHT }} €</b-col>
                <b-col md="6"><strong>Prix annuel HT :</strong> {{ dossier.formule.prixAnnuelHT }} €</b-col>
                <b-col md="6"><strong>Facturation :</strong> {{ dossier.C_facturation }}</b-col>
                <b-col md="6"><strong>Code promo :</strong> {{ dossier.C_codePromo || '—' }}</b-col>
              </b-row>
            </b-card>

            <b-card class="mt-2">
              <h5>Localisation des archives</h5>
              <b-row v-if="dossier.C_archivesAdresse">
                <b-col md="12">
                  <strong>Adresse :</strong>
                  {{ dossier.C_archivesAdresse }} {{ dossier.C_archivesComplement }},
                  {{ dossier.C_archivesCp }} {{ dossier.C_archivesVille }},
                  {{ dossier.C_archivesPays }}
                </b-col>
                <b-col md="6">
                  <strong>Type de lieu :</strong>
                  {{ labelTypeLieu(dossier.C_archivesTypeLieu, dossier.C_archivesTypeLieuAutre) }}
                </b-col>
                <b-col md="6">
                  <strong>Responsable :</strong>
                  {{ dossier.C_archivesResponsable || '—' }}
                  <span v-if="dossier.C_archivesFonction">({{ dossier.C_archivesFonction }})</span>
                </b-col>
              </b-row>
              <p
                v-else
                class="text-muted mb-0"
              >
                Non renseigné.
              </p>
            </b-card>

            <b-card class="mt-2">
              <h5>Lutte contre le blanchiment (LCB-FT)</h5>
              <b-row>
                <b-col md="12">
                  <strong>Statut PPE :</strong>
                  <b-badge :variant="dossier.B_personneExposee ? 'warning' : 'success'">
                    {{ dossier.B_personneExposee ? 'Personne politiquement exposée' : 'Non' }}
                  </b-badge>
                </b-col>
                <template v-if="dossier.B_personneExposee">
                  <b-col md="6">
                    <strong>Catégorie :</strong>
                    {{ labelPpeCategorie(dossier.C_ppeCategorie, dossier.C_ppeCategorieAutre) }}
                  </b-col>
                  <b-col md="6">
                    <strong>Lien familial / pro :</strong>
                    {{ dossier.B_ppeLien ? labelPpeLien(dossier.C_ppeLienType, dossier.C_ppeLienAutre) : 'Aucun' }}
                  </b-col>
                  <b-col
                    v-if="dossier.B_ppeLien && dossier.C_ppeLienNom"
                    md="12"
                  >
                    <strong>Détails du lien :</strong>
                    {{ dossier.C_ppeLienNom }}
                    <span v-if="dossier.C_ppeLienFonction">— {{ dossier.C_ppeLienFonction }}</span>
                    <span v-if="dossier.C_ppeLienPays">({{ dossier.C_ppeLienPays }})</span>
                  </b-col>
                </template>
                <b-col md="6">
                  <strong>Origine des fonds :</strong>
                  {{ labelOrigineFonds(dossier.C_origineFonds, dossier.C_origineFondsAutre) }}
                </b-col>
                <b-col md="6">
                  <strong>Pays d'origine des fonds :</strong>
                  {{ dossier.C_origineFondsPays || '—' }}
                </b-col>
                <b-col md="12">
                  <strong>Activités pays à risque GAFI :</strong>
                  <b-badge :variant="dossier.B_paysRisqueGafi ? 'danger' : 'success'">
                    {{ dossier.B_paysRisqueGafi ? 'Oui' : 'Non' }}
                  </b-badge>
                  <span v-if="dossier.B_paysRisqueGafi && dossier.C_paysRisqueGafiPrecisions">
                    — {{ dossier.C_paysRisqueGafiPrecisions }}
                  </span>
                </b-col>
                <b-col md="12">
                  <strong>Gel des avoirs :</strong>
                  <b-badge :variant="dossier.B_gelAvoirs ? 'danger' : 'success'">
                    {{ dossier.B_gelAvoirs ? 'Oui' : 'Non' }}
                  </b-badge>
                  <span v-if="dossier.B_gelAvoirs && dossier.C_gelAvoirsPrecisions">
                    — {{ dossier.C_gelAvoirsPrecisions }}
                  </span>
                </b-col>
                <b-col md="12">
                  <strong>Procédures judiciaires (5 ans) :</strong>
                  <b-badge :variant="dossier.B_proceduresJudiciaires ? 'danger' : 'success'">
                    {{ dossier.B_proceduresJudiciaires ? 'Oui' : 'Non' }}
                  </b-badge>
                  <span v-if="dossier.B_proceduresJudiciaires && dossier.C_proceduresJudiciairesPrecisions">
                    — {{ dossier.C_proceduresJudiciairesPrecisions }}
                  </span>
                </b-col>
              </b-row>
            </b-card>

            <b-card class="mt-2">
              <h5>Mandat de prélèvement SEPA</h5>
              <b-row>
                <b-col md="6">
                  <strong>Code client Domicializ :</strong>
                  <code>{{ dossier.C_codeClient || '—' }}</code>
                </b-col>
                <b-col md="6">
                  <strong>Titulaire du compte :</strong>
                  {{ dossier.C_sepaTitulaire || '—' }}
                </b-col>
                <b-col md="8">
                  <strong>IBAN :</strong>
                  <code>{{ formatIban(dossier.C_sepaIban) || '—' }}</code>
                </b-col>
                <b-col md="4">
                  <strong>BIC :</strong>
                  <code>{{ dossier.C_sepaBic || '—' }}</code>
                </b-col>
              </b-row>
            </b-card>
          </b-tab>

          <b-tab :title="`Documents (${dossier.documents.length})`">
            <b-card>
              <p
                v-if="dossier.B_archive"
                class="text-warning"
              >
                Ce dossier est archivé : les pièces jointes ont été supprimées du disque.
                Les métadonnées ci-dessous sont conservées à titre d'historique.
              </p>
              <b-table
                :items="dossier.documents"
                :fields="docFields"
                responsive
                striped
              >
                <template #cell(taille)="row">
                  {{ formatTaille(row.item.taille) }}
                </template>
                <template #cell(date)="row">
                  {{ formatDate(row.item.date) }}
                </template>
                <template #cell(actions)="row">
                  <b-button
                    v-if="row.item.presentSurDisque"
                    size="sm"
                    variant="primary"
                    :disabled="row.item.downloading"
                    @click="telecharger(row.item)"
                  >
                    <b-spinner
                      v-if="row.item.downloading"
                      small
                    />
                    <span v-else>Télécharger</span>
                  </b-button>
                  <b-badge
                    v-else
                    variant="dark"
                  >
                    Archivé
                  </b-badge>
                </template>
              </b-table>
            </b-card>
          </b-tab>

          <b-tab title="Documents générés">
            <b-card>
              <div class="d-flex justify-content-between align-items-start mb-1">
                <p class="text-muted mb-0">
                  Documents PDF reconstruits à la demande à partir des informations
                  saisies par le domicilié et des modèles en vigueur. Rien n'est
                  stocké : chaque consultation régénère le document, une correction
                  de modèle est donc reprise immédiatement.
                </p>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="ml-1 text-nowrap"
                  :disabled="dossierCompletEnCours"
                  @click="regenererDossierComplet"
                >
                  <b-spinner
                    v-if="dossierCompletEnCours"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="RefreshCwIcon"
                  />
                  Tout régénérer
                </b-button>
              </div>
              <b-alert
                :show="donneesManquantes.length > 0"
                variant="warning"
                class="p-1"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  class="mr-1"
                />
                Dossier incomplet : {{ donneesManquantes.join(', ') }}.
                Les documents générés maintenant sortiront avec ces
                informations vides.
              </b-alert>
              <b-list-group>
                <b-list-group-item
                  v-for="doc in documentsGeneres"
                  :key="doc.type"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <feather-icon
                      icon="FileTextIcon"
                      class="mr-1 text-primary"
                    />
                    {{ doc.label }}
                  </div>
                  <div>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="mr-1"
                      :disabled="!!doc.busy"
                      @click="ouvrirPdfGenere(doc)"
                    >
                      <b-spinner
                        v-if="doc.busy === 'voir'"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="EyeIcon"
                      />
                      Régénérer et consulter
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      :disabled="!!doc.busy"
                      @click="telechargerPdfGenere(doc)"
                    >
                      <b-spinner
                        v-if="doc.busy === 'telecharger'"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="DownloadIcon"
                      />
                      Télécharger
                    </b-button>
                  </div>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center bg-light">
                  <div>
                    <feather-icon
                      icon="ClipboardIcon"
                      class="mr-1 text-success"
                    />
                    <strong>PDF récapitulatif du dossier</strong>
                  </div>
                  <b-button
                    size="sm"
                    variant="success"
                    @click="telechargerRecap"
                  >
                    <feather-icon icon="DownloadIcon" />
                    Télécharger
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-tab>

          <b-tab title="Paiement">
            <b-card>
              <p>
                <strong>Statut :</strong>
                <b-badge :variant="dossier.paiement.paye ? 'success' : 'secondary'">
                  {{ dossier.paiement.paye ? 'Payé' : 'Non payé' }}
                </b-badge>
              </p>
              <p v-if="dossier.formule">
                <strong>Montant {{ dossier.C_facturation === 'annuelle' ? 'annuel' : 'mensuel' }} HT :</strong>
                {{ dossier.C_facturation === 'annuelle' ? dossier.formule.prixAnnuelHT : dossier.formule.prixMensuelHT }} €
              </p>
            </b-card>
          </b-tab>
        </b-tabs>
      </div>

      <b-modal
        v-model="showArchiveModal"
        title="Archiver le dossier"
        ok-title="Confirmer l'archivage"
        ok-variant="danger"
        cancel-title="Annuler"
        @ok="archiver"
      >
        <p>
          Cette action <strong>supprime définitivement du disque</strong> toutes les pièces
          jointes uploadées par le client. Les métadonnées (nom, taille, date) sont
          conservées en base pour audit.
        </p>
        <p>Cette opération est irréversible. Confirmer ?</p>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BTabs,
  BTab,
  BTable,
  BFormTextarea,
  BContainer,
  BSpinner,
  BModal,
  BListGroup,
  BListGroupItem,
  BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BTabs,
    BTab,
    BTable,
    BFormTextarea,
    BContainer,
    BSpinner,
    BModal,
    BListGroup,
    BListGroupItem,
    BAlert,
    AdminNav,
  },
  data() {
    return {
      admin: null,
      dossier: null,
      note: '',
      showArchiveModal: false,
      relanceEnCours: false,
      docFields: [
        { key: 'libelleRequis', label: 'Type de document' },
        { key: 'nomOriginal', label: 'Nom du fichier' },
        { key: 'mimeType', label: 'Type' },
        { key: 'taille', label: 'Taille' },
        { key: 'date', label: 'Reçu le' },
        { key: 'actions', label: '' },
      ],
      dossierCompletEnCours: false,
      documentsGeneres: [
        {
          type: 'contrat',
          label: 'Contrat de domiciliation',
          filename: 'contrat-domiciliation.pdf',
          busy: '',
        },
        {
          type: 'procuration',
          label: 'Procuration postale',
          filename: 'procuration-postale.pdf',
          busy: '',
        },
        {
          type: 'attestation-creation',
          label: "Attestation sur l'honneur de création",
          filename: 'attestation-creation.pdf',
          busy: '',
        },
        {
          type: 'attestation-ppe',
          label: 'Formulaire Personne Politiquement Exposée',
          filename: 'formulaire-ppe.pdf',
          busy: '',
        },
        {
          type: 'localisation-archives',
          label: 'Localisation des archives',
          filename: 'localisation-archives.pdf',
          busy: '',
        },
      ],
    }
  },
  computed: {
    // Les documents sont reconstruits depuis la base : tant que le
    // domicilié n'a pas validé l'étape « demandeur », le contrat sort avec
    // un bloc d'identité vide. On le dit ici plutôt que de laisser
    // découvrir un PDF incomplet.
    donneesManquantes() {
      const d = this.dossier
      if (!d || !d.UI) return []
      const manque = []
      if (!d.C_nomRL || !d.C_prenomRL) manque.push('identité du représentant')
      if (!d.D_dateNaissance) manque.push('date de naissance')
      if (!d.C_adresseRL && !d.C_cpRL && !d.C_villeRL) manque.push('adresse personnelle')
      if (!d.formule) manque.push('formule')
      return manque
    },
  },
  created() {
    const raw = localStorage.getItem('domAdminData')
    if (raw) this.admin = JSON.parse(raw)
    this.loadDossier()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },
    loadDossier() {
      const UI = this.$route.params.UIdemande
      this.$http.get(`/admindom/dossier/${UI}`, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            const d = response.data.dossier
            d.documents = d.documents.map(x => ({ ...x, downloading: false }))
            this.dossier = d
            this.note = d.C_noteAdmin || ''
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: { title: 'Dossier introuvable', icon: 'AlertTriangleIcon', variant: 'danger' },
            })
          }
        })
        .catch(err => this.handleAuthError(err))
    },
    marquerTraite() {
      this.$http.post('/admindom/traiter', {
        UIdemande: this.dossier.UI,
        note: this.note,
      }, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.toast('Dossier marqué traité', 'success', 'CheckCircleIcon')
            this.loadDossier()
          }
        })
        .catch(err => this.handleAuthError(err))
    },
    annulerTraitement() {
      this.$http.post('/admindom/annulertraitement', { UIdemande: this.dossier.UI }, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.toast('Traitement annulé', 'warning', 'RotateCcwIcon')
            this.loadDossier()
          }
        })
        .catch(err => this.handleAuthError(err))
    },
    archiver() {
      this.$http.post('/admindom/archiver', { UIdemande: this.dossier.UI }, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.toast(
              `Dossier archivé (${response.data.nbFichiersSupprimes} fichier(s) supprimé(s))`,
              'success',
              'ArchiveIcon',
            )
            this.loadDossier()
          }
        })
        .catch(err => this.handleAuthError(err))
    },
    telecharger(doc) {
      // Le JWT est injecté par l'interceptor axios sur x-access-token.
      doc.downloading = true
      this.$http.get(`/admindom/document/${doc.UI}`, { responseType: 'blob', ...this.authHeaders() })
        .then(response => {
          const blob = new Blob([response.data], { type: doc.mimeType || 'application/octet-stream' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = doc.nomOriginal
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          doc.downloading = false
        })
        .catch(err => {
          doc.downloading = false
          this.handleAuthError(err)
          this.toast('Téléchargement impossible', 'danger', 'AlertTriangleIcon')
        })
    },
    retour() {
      this.$router.push({ name: 'admin-dom-dossiers' })
    },
    logout() {
      localStorage.removeItem('domAdminToken')
      localStorage.removeItem('domAdminData')
      this.$router.push({ name: 'admin-dom-login' })
    },
    handleAuthError(err) {
      if (err && err.response && (err.response.status === 401 || err.response.status === 403)) {
        this.logout()
      }
    },
    formatDate(d, dateOnly = false) {
      if (!d) return ''
      const dt = new Date(d)
      if (dateOnly) return dt.toLocaleDateString('fr-FR')
      return `${dt.toLocaleDateString('fr-FR')} ${dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    },
    formatTaille(octets) {
      if (!octets) return '—'
      if (octets < 1024) return `${octets} o`
      if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(1)} Ko`
      return `${(octets / (1024 * 1024)).toFixed(2)} Mo`
    },
    formatIban(iban) {
      if (!iban) return ''
      return String(iban).replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
    },
    labelTypeLieu(code, autre) {
      const map = {
        localPro: 'Local professionnel',
        domicileDirigeant: 'Domicile du dirigeant',
        expertComptable: "Cabinet d'expertise comptable",
        autre: autre || 'Autre',
      }
      return map[code] || code || '—'
    },
    labelPpeCategorie(code, autre) {
      const map = {
        chefEtat: "Chef d'État, parlementaire",
        magistrat: 'Haut magistrat, militaire de haut rang',
        ambassadeur: "Ambassadeur, dirigeant d'entreprise publique",
        parti: "Direction d'un parti politique",
        autre: autre || 'Autre',
      }
      return map[code] || code || '—'
    },
    labelPpeLien(code, autre) {
      const map = {
        conjoint: 'Conjoint(e)',
        familleProche: 'Parent / enfant / frère / sœur',
        associe: "Partenaire d'affaires",
        autre: autre || 'Autre',
      }
      return map[code] || code || '—'
    },
    labelOrigineFonds(code, autre) {
      const map = {
        revenus: 'Revenus professionnels',
        epargne: 'Épargne personnelle',
        venteActifs: "Vente d'actifs",
        autre: autre || 'Autre',
      }
      return map[code] || code || '—'
    },
    relancerClient() {
      this.relanceEnCours = true
      this.$http.post(`/admindom/dossier/${this.dossier.UI}/relance`, {}, this.authHeaders())
        .then(response => {
          this.relanceEnCours = false
          if (response.data.res === 1) {
            this.toast('Email de relance envoyé au client', 'success', 'CheckIcon')
          } else {
            this.toast(response.data.message || 'Échec de la relance', 'danger', 'AlertTriangleIcon')
          }
        })
        .catch(() => {
          this.relanceEnCours = false
          this.toast('Erreur de connexion', 'danger', 'AlertTriangleIcon')
        })
    },
    toast(title, variant, icon) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: { title, variant, icon },
      })
    },
    // Les documents générés passent par l'API admin (JWT) et non par
    // l'endpoint public /dom/genererPdf : ils contiennent des données
    // personnelles du domicilié. Chaque appel régénère le PDF.
    recupererPdfGenere(doc, mode) {
      this.$set(doc, 'busy', mode)
      return this.$http.get(
        `/admindom/dossier/${this.dossier.UI}/document-genere/${doc.type}`,
        { responseType: 'blob', ...this.authHeaders() },
      )
        .then(response => {
          this.$set(doc, 'busy', '')
          return new Blob([response.data], { type: 'application/pdf' })
        })
        .catch(err => {
          this.$set(doc, 'busy', '')
          this.handleAuthError(err)
          this.toast(`Régénération impossible : ${doc.label}`, 'danger', 'AlertTriangleIcon')
          return null
        })
    },
    ouvrirPdfGenere(doc) {
      this.recupererPdfGenere(doc, 'voir').then(blob => {
        if (!blob) return
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        // Le blob doit survivre au temps d'ouverture de l'onglet.
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      })
    },
    telechargerPdfGenere(doc) {
      this.recupererPdfGenere(doc, 'telecharger').then(blob => {
        if (!blob) return
        this.enregistrerBlob(blob, doc.filename)
      })
    },
    regenererDossierComplet() {
      this.dossierCompletEnCours = true
      const { UI } = this.dossier
      this.$http.get(`/admindom/dossier/${UI}/documents-generes/pdf`, {
        responseType: 'blob',
        ...this.authHeaders(),
      })
        .then(response => {
          this.dossierCompletEnCours = false
          this.enregistrerBlob(new Blob([response.data], { type: 'application/pdf' }), `dossier-${UI}.pdf`)
          const echecs = parseInt(response.headers['x-documents-echecs'], 10) || 0
          if (echecs) {
            this.toast(`${echecs} document(s) n'ont pas pu être générés`, 'warning', 'AlertTriangleIcon')
          } else {
            this.toast('Documents régénérés', 'success', 'CheckIcon')
          }
        })
        .catch(err => {
          this.dossierCompletEnCours = false
          this.handleAuthError(err)
          this.toast('Régénération impossible', 'danger', 'AlertTriangleIcon')
        })
    },
    enregistrerBlob(blob, nomFichier) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomFichier
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    telechargerRecap() {
      const { UI } = this.dossier
      const token = localStorage.getItem('domAdminToken')
      this.$http.get(`/admindom/dossier/${UI}/recap-pdf`, {
        headers: { 'x-access-token': token },
        responseType: 'blob',
      })
        .then(response => {
          this.enregistrerBlob(new Blob([response.data], { type: 'application/pdf' }), `recap-${UI}.pdf`)
        })
        .catch(() => {
          this.toast('Erreur lors du téléchargement du récap', 'danger', 'AlertTriangleIcon')
        })
    },
  },
}
</script>

<style lang="scss">
.admin-dom-wrapper {
  min-height: 100vh;
  background-color: #f8f8f8;
  padding-bottom: 2rem;
}
</style>
